// Typed client for /api/dashboard. Cookie-authenticated (the same admin
// session cookie every other page uses), so there's no token to manage
// here — a 401 just means "signed out", which the caller surfaces rather
// than trying to recover from.

import type { InteractionConfig } from '@chat-widget/shared/src/interactions';
import type { ProfileDetails, ProfileField } from '@chat-widget/shared/src/profile';

/** The account profile (/app/profile; server/src/auth/profile.ts). */
export interface Profile extends ProfileDetails {
  email: string;
  pictureUrl: string | null;
  signInWith: string[];
  createdAt: number;
}

/** One field's change, from the account's profile history. */
export interface ProfileChange {
  field: ProfileField;
  oldValue: string | null;
  newValue: string | null;
  changedAt: number;
}

/** A chat-bubble animation one customer uploaded for one bot. */
export interface UploadedLauncher {
  /** `<hash>.<ext>` — its id within the bot. */
  file: string;
  /** Root-relative; same value works on localhost and in production. */
  url: string;
  contentType: string;
  size: number;
  filename: string;
  uploadedAt: number;
}

export interface Bot {
  id: string;
  userId: number | null;
  /** The owner's own label for the bot, here in the dashboard only. */
  name: string;
  /** The chat header visitors see; null means "same as the name". */
  title: string | null;
  greeting: string;
  accentColor: string;
  /** Phase 11: null means "use the default built-in icon". */
  logoUrl: string | null;
  model: string;
  dailyTokenCap: number;
  tools: string[];
  recrawlEnabled: boolean;
  createdAt: number;
  logRetentionDays: number;
  loggingNotice: string | null;
  /** Phase 12: starters, the contact form and link buttons — the
   * Interactions tab. Always present (the server fills in defaults). */
  interactions: InteractionConfig;
}

// ---- plan, trial and usage (server/src/billing/entitlements.ts) --------

export type AccessLevel = 'none' | 'trial' | 'growth' | 'basic' | 'professional' | 'enterprise' | 'expired' | 'unlimited';

/** The levels that mean "paying customer, on a named plan" — everything
 * with its own Dodo product (server/src/billing/entitlements.ts's
 * AccessLevel). Kept as one list so adding a tier doesn't mean hunting
 * down every `level === 'growth' || …` chain in the dashboard. */
const PAID_LEVELS: readonly AccessLevel[] = ['growth', 'basic', 'professional', 'enterprise'];

export function isPaidLevel(level: AccessLevel): boolean {
  return PAID_LEVELS.includes(level);
}

/** Signed in but not on a plan yet: every /app page starts the free trial
 * first (Dashboard.tsx's PlanGate), and until then the site header's
 * account menu offers only Sign out (Site.tsx). One that has paid and is
 * only waiting on its confirmation is past this — the dashboard is theirs
 * already. */
export function choosingPlan(account: Pick<AccountInfo, 'level' | 'pendingCheckout'>): boolean {
  return account.level === 'none' && !account.pendingCheckout?.returned;
}

/** The plan's name as a customer would recognise it from the pricing page. */
export function planLabel(level: AccessLevel): string {
  // 'enterprise' is sold as "Business" and 'basic' as "Starter" — the stored names never changed
  // (widget/src/site/pricingPlans.tsx says why).
  if (level === 'enterprise') return 'Business';
  if (level === 'basic') return 'Starter';
  return level.charAt(0).toUpperCase() + level.slice(1);
}

/** A checkout the account started that no Dodo webhook has resolved yet
 * (server/src/billing/index.ts's PendingCheckout). Server-held, so the
 * "confirming your payment" banner survives a refresh, a tab click and a
 * new tab — it used to be inferred from the ?checkout=success query param
 * alone, which any of those three threw away. */
export interface PendingCheckout {
  plan: AccessLevel;
  /** True once the customer came back on Dodo's success redirect — i.e.
   * they got as far as paying, rather than closing the checkout tab. */
  returned: boolean;
  waitingMs: number;
}

export interface AccountInfo {
  level: AccessLevel;
  limits: {
    bots: number;
    pages: number;
    messages: number;
    messagesPer: 'trial' | 'month';
    unansweredReports: boolean;
    /** Automatic re-crawl: monthly on Basic, weekly on the higher plans,
     * off on the trial (server/src/billing/entitlements.ts). */
    autoRefresh: 'off' | 'monthly' | 'weekly';
    /** "Powered by VolgaChat": 'shown' always, 'addon' = shown unless the
     * remove-badge add-on is bought, 'removed' = never (entitlements.ts). */
    badge: 'shown' | 'addon' | 'removed';
  };
  expiredReason: 'trial_ended' | 'plan_ended' | null;
  trialEndsAt: number | null;
  messagesUsed: number;
  pagesUsed: number;
  botsUsed: number;
  billingStatus: string | null;
  pendingCheckout: PendingCheckout | null;
  /** When a paid plan cancelled at the end of its period stops (epoch ms);
   * it keeps working until then. Null when it isn't ending. */
  planEndsAt: number | null;
  /** The remove-badge add-on is bought and active. */
  removeBadgeAddon: boolean;
  /** Whether this account's bots show "Powered by VolgaChat" right now. */
  showBadge: boolean;
  /** An admin's own daily, weekly and monthly message limits and their
   * use; only on the admin's unlimited account
   * (server/src/billing/entitlements.ts). */
  messageCaps?: MessageCap[];
}

export type LimitPeriod = 'day' | 'week' | 'month';

export interface MessageCap {
  period: LimitPeriod;
  key: string;
  /** null = no limit for this period. */
  limit: number | null;
  used: number;
  /** When it starts again from zero (epoch ms, a UTC midnight). */
  resetsAt: number;
}

/** The first of an admin's limits their bots have used up — when set,
 * the bots have stopped answering (server/src/billing/allowance.ts). */
export function reachedCap(account: Pick<AccountInfo, 'messageCaps'>): MessageCap | null {
  return account.messageCaps?.find((cap) => cap.limit !== null && cap.used >= cap.limit) ?? null;
}

export const CAP_LABELS: Record<LimitPeriod, { name: string; current: string }> = {
  day: { name: 'Daily', current: 'Today' },
  week: { name: 'Weekly', current: 'This week' },
  month: { name: 'Monthly', current: 'This month' },
};

// ---- leads ------------------------------------------------------------

/** A contact a visitor left through a chatbot's contact form
 * (server/src/leads/). */
/** An extra address that gets the account's lead alerts
 * (server/src/leads/recipients.ts). */
export interface LeadRecipient {
  id: string;
  email: string;
  createdAt: number;
  /** null until the address clicks Confirm in its email. */
  confirmedAt: number | null;
  lastInvitedAt: number;
}

export interface Lead {
  id: string;
  botId: string;
  botName: string;
  conversationId: string;
  createdAt: number;
  fields: { label: string; value: string }[];
  pageUrl: string | null;
  /** The email to the owner: sent, still sending, failed, or skipped (no
   * email sending on this deployment). */
  notifyStatus: 'pending' | 'sent' | 'failed' | 'skipped';
  notifyError: string | null;
}

// ---- usage and billing pages ------------------------------------------

export interface UsagePeriod {
  /** 'YYYY-MM' for a paid month, or 'trial' for the trial's one bucket. */
  period: string;
  messages: number;
}

/** A payment as Dodo reports it. Amounts are in the currency's smallest
 * unit (cents for USD) — formatted at the point of display. */
export interface PaymentRow {
  paymentId: string;
  createdAt: number;
  totalAmount: number;
  currency: string;
  status: string;
  invoiceUrl: string | null;
  /** "upi", "card", … as Dodo names it; null when it doesn't say. */
  paymentMethod?: string | null;
  cardLastFour?: string | null;
  /** "new", "renewal", "plan_change" or "on_demand". */
  chargeType?: string | null;
}

/** A payment the Billing page explains in full because it's holding
 * something up: still settling (no plan change until it does), or the one
 * a plan change made — succeeded, or failed and why. From
 * server/src/billing/paymentStatus.ts. */
export interface PaymentStatus {
  paymentId: string;
  state: 'waiting' | 'failed' | 'succeeded' | 'other';
  /** Dodo's own status, e.g. "processing", "requires_customer_action". */
  status: string;
  chargeType: string | null;
  amount: number;
  currency: string;
  method: string | null;
  cardLastFour: string | null;
  createdAt: number;
  updatedAt: number | null;
  errorCode: string | null;
  /** Dodo's explanation of a failure, ids taken out. */
  errorMessage: string | null;
}

export interface SubscriptionInfo {
  status: string;
  recurringAmount: number | null;
  currency: string | null;
  paymentFrequencyInterval: string | null;
  paymentFrequencyCount: number | null;
  nextBillingAt: number | null;
  cancelAtNextBillingDate: boolean;
}

export interface BillingOverview {
  account: AccountInfo;
  plan: string;
  status: string | null;
  planChangedAt: number | null;
  /** A change to this paid plan was requested and isn't confirmed yet —
   * the page shows its progress rather than offering it again. */
  pendingPlanChange?: string | null;
  hasDodoRecord: boolean;
  /** The Remove branding add-on can be bought (its checkout is configured). */
  removeBadgeCheckout: boolean;
  /** Set when the customer has cancelled Remove branding: the date it
   * stops (epoch ms). Until then it can be kept. */
  removeBadgeCancelsAt: number | null;
  /** The Dodo identifiers behind this account's billing, for matching a
   * subscription to its product in Dodo. */
  reference: {
    subscriptionId: string | null;
    customerId: string | null;
    productId: string | null;
    /** "Business · monthly" — null when the product isn't known. */
    productLabel: string | null;
    addonSubscriptionId: string | null;
  };
  /** The add-on's last payment failed and is being retried — the badge is
   * showing until it goes through. */
  removeBadgePaymentFailed: boolean;
  /** False when this deployment has no Dodo API key, so the payment
   * history and portal sections are unavailable rather than empty. */
  providerConfigured: boolean;
  /** Set when Dodo was asked but couldn't answer — shown to the customer
   * instead of an empty table that would read as "no payments". */
  providerError: string | null;
  /** The payment holding something up right now, explained — null when
   * nothing is. */
  paymentStatus?: PaymentStatus | null;
  subscription: SubscriptionInfo | null;
  payments: PaymentRow[];
  portalAvailable: boolean;
}

// ---- phase 10: conversation logs --------------------------------------

export type EndedReason = 'ok' | 'error' | 'rate_limit' | 'daily_cap' | 'plan_limit' | 'unanswered';

export interface ConversationRow {
  id: string;
  botId: string;
  startedAt: number;
  lastMessageAt: number;
  messageCount: number;
  inputTokens: number;
  outputTokens: number;
  visitorHash: string | null;
  pageUrl: string | null;
  endedReason: EndedReason | null;
  firstUserMessage: string | null;
  failed: boolean;
  /** When it was deleted (moved to Archived); null for a listed one. */
  archivedAt: number | null;
}

/** Which conversations an archive or restore applies to. `batch` (restore
 * only) is the stamp an archive answered with — what Undo puts back. */
export type ConversationTarget = { ids: string[] } | { all: true } | { batch: number };

export interface LoggedMessage {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant';
  content: string;
  parts: Array<Record<string, unknown>> | null;
  createdAt: number;
  toolCalls: string[] | null;
  grounded: boolean | null;
  errorCode: string | null;
  sources: string[] | null;
}

export interface UnansweredGroup {
  question: string;
  count: number;
  lastAskedAt: number;
  variants: Array<{ question: string; askedAt: number; conversationId: string }>;
}

/** One chatbot's month (server/src/analytics/). */
export interface BotAnalytics {
  month: string;
  months: string[];
  conversations: number;
  leads: number;
  unanswered: number;
  topQuestions: Array<{ question: string; count: number }>;
  questionsPartial: boolean;
  retentionDays: number;
}

export interface LogSummary {
  conversations7d: number;
  conversations30d: number;
  messages7d: number;
  messages30d: number;
  ungroundedRate30d: number;
  ungroundedCount30d: number;
  answered30d: number;
  retentionDays: number;
}

export interface BotOrigin {
  botId: string;
  domain: string;
  verified: boolean;
  verifiedAt: number | null;
  verificationToken: string;
  createdAt: number;
  /** When the widget last loaded on this domain — null until the embed
   * snippet is actually on the customer's site. */
  widgetSeenAt: number | null;
  /** The address typed when the site was added on the Install step, path
   * and all — where the one-click crawl starts. Null on older rows. */
  siteUrl: string | null;
}

export type SourceStatus = 'pending' | 'discovering' | 'discovered' | 'indexing' | 'processing' | 'ready' | 'failed';

export interface Source {
  id: string;
  botId: string;
  type: 'url' | 'file';
  urlOrFilename: string;
  status: SourceStatus;
  error: string | null;
  pageCount: number;
  lastRun: number | null;
  createdAt: number;
}

export interface SourcePage {
  id: string;
  sourceId: string;
  url: string;
  title: string | null;
  selected: boolean;
  status: 'pending' | 'indexed' | 'failed' | 'skipped';
  error: string | null;
  charCount: number;
}

export interface QaOverride {
  id: string;
  botId: string;
  question: string;
  answer: string;
  createdAt: number;
}

export interface Coverage {
  chunkCount: number;
  totalChars: number;
  estimatedTokens: number;
  budgetTokens: number;
  fitsInBudget: boolean;
  exceedsScoringLimit: boolean;
  scoringLimit: number;
}

export interface BotDetail {
  bot: Bot;
  origins: BotOrigin[];
  sources: Source[];
  coverage: Coverage;
  verification: { metaName: string; wellKnownPath: string };
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly data: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

/** Failures the customer can't act on as worded by the server get plain
 * wording here: what happened and what to do next. Refusals they CAN act
 * on (a plan limit, a bad input) keep the server's own message. */
const NETWORK_ERROR_MESSAGE = "We couldn't reach VolgaChat. Check your internet connection and try again.";
const SIGNED_OUT_MESSAGE = "You've been signed out. Refresh the page to sign in again.";
const SERVER_ERROR_MESSAGE =
  "That didn't work because of a problem on our side. Please try again in a moment — if it keeps happening, email support@volgachat.com.";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  return requestAt<T>('/api/dashboard', path, init);
}

/** `request` against another API root — /api/admin for the sales demos. */
async function requestAt<T>(base: string, path: string, init: RequestInit = {}): Promise<T> {
  const isForm = init.body instanceof FormData;
  let res: Response;
  try {
    res = await fetch(`${base}${path}`, {
      ...init,
      headers: isForm ? init.headers : { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    });
  } catch {
    // Offline, DNS, a dropped connection — no response at all.
    throw new ApiError(NETWORK_ERROR_MESSAGE, 0);
  }

  let data: Record<string, unknown> = {};
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    // Empty or non-JSON body — the status still tells us what happened.
  }
  if (!res.ok) {
    const message =
      res.status === 401
        ? SIGNED_OUT_MESSAGE
        : res.status >= 500 && !(res.status === 502 || res.status === 503)
          ? SERVER_ERROR_MESSAGE
          : typeof data.error === 'string' && data.error
            ? data.error
            : SERVER_ERROR_MESSAGE;
    throw new ApiError(message, res.status, data);
  }
  return data as T;
}

const json = (body: unknown) => JSON.stringify(body);

export const dashboardApi = {
  getAccount: () => request<{ account: AccountInfo }>('/account'),
  /** The only call that starts the trial clock — see StartingTrial
   * in Dashboard.tsx and POST /api/dashboard/trial/start. */
  startTrial: () => request<{ account: AccountInfo }>('/trial/start', { method: 'POST' }),
  /** Tells the server the browser just came back from Dodo's success
   * redirect, so the pending-payment banner keeps showing after this page
   * view ends. See POST /api/dashboard/checkout/returned. */
  checkoutReturned: () => request<{ account: AccountInfo }>('/checkout/returned', { method: 'POST' }),
  getUsage: () => request<{ account: AccountInfo; history: UsagePeriod[] }>('/usage'),
  getBilling: () => request<BillingOverview>('/billing'),
  /** Mints a one-time link into Dodo's hosted portal. Never stored — the
   * caller redirects to it straight away. */
  cancelRemoveBadge: () =>
    request<{ ok: true; cancelsAt: number | null; account: AccountInfo }>('/billing/addons/remove-badge/cancel', { method: 'POST' }),
  resumeRemoveBadge: () =>
    request<{ ok: true; cancelsAt: number | null; account: AccountInfo }>('/billing/addons/remove-badge/resume', { method: 'POST' }),
  billingPortal: () => request<{ url: string }>('/billing/portal', { method: 'POST' }),
  /** Moves the existing subscription onto another plan (prorated) — see
   * POST /api/dashboard/billing/change-plan. `uncertain`: Dodo didn't
   * answer in time, so whether it happened is being checked. A refusal
   * because a payment is still settling carries that payment as
   * ApiError.data.payment. */
  changePlan: (plan: string, cycle: 'monthly' | 'yearly') =>
    request<{ ok: true; uncertain?: boolean; account: AccountInfo }>('/billing/change-plan', { method: 'POST', body: json({ plan, cycle }) }),
  /** Has a requested plan change gone through? Asks Dodo directly, for when
   * its webhook is slow or never comes — 'applied' means the new plan is
   * on the account now, 'failed' that its payment failed (the plan stays
   * as it was), 'not_started' that Dodo has no sign of it. `payment` is the
   * payment behind it, when there is one. */
  confirmPlanChange: () =>
    request<{
      result: 'applied' | 'unchanged' | 'unavailable' | 'failed' | 'not_started';
      payment: PaymentStatus | null;
      account: AccountInfo;
    }>('/billing/change-plan/confirm', { method: 'POST' }),
  listBots: () => request<{ bots: Bot[] }>('/bots'),
  createBot: (name: string, greeting?: string) => request<{ bot: Bot }>('/bots', { method: 'POST', body: json({ name, greeting }) }),
  getBot: (botId: string) => request<BotDetail>(`/bots/${botId}`),
  /** `init` is for the autosave's last-moment flush: `{ keepalive: true }`
   * lets the request outlive the page it was sent from. */
  updateBot: (botId: string, patch: Partial<Bot>, init: RequestInit = {}) =>
    request<{ bot: Bot }>(`/bots/${botId}`, { method: 'PATCH', body: json(patch), ...init }),
  deleteBot: (botId: string) => request<{ ok: true }>(`/bots/${botId}`, { method: 'DELETE' }),

  addOrigin: (botId: string, domain: string) =>
    request<{ origin: BotOrigin }>(`/bots/${botId}/origins`, { method: 'POST', body: json({ domain }) }),
  deleteOrigin: (botId: string, domain: string) =>
    request<{ ok: true }>(`/bots/${botId}/origins/${encodeURIComponent(domain)}`, { method: 'DELETE' }),
  verifyOrigin: (botId: string, domain: string, sourceUrl?: string) =>
    request<{ verified: boolean; method?: string; error?: string }>(`/bots/${botId}/origins/${encodeURIComponent(domain)}/verify`, {
      method: 'POST',
      body: json({ sourceUrl }),
    }),

  listSources: (botId: string) => request<{ sources: Source[] }>(`/bots/${botId}/sources`),
  addUrlSource: (botId: string, url: string) => request<{ source: Source }>(`/bots/${botId}/sources/url`, { method: 'POST', body: json({ url }) }),
  uploadFile: (botId: string, file: File) => {
    const form = new FormData();
    form.set('file', file);
    return request<{ source: Source; ok: boolean; error?: string }>(`/bots/${botId}/sources/file`, { method: 'POST', body: form });
  },
  deleteSource: (botId: string, sourceId: string) => request<{ ok: true }>(`/bots/${botId}/sources/${sourceId}`, { method: 'DELETE' }),
  recrawl: (botId: string, sourceId: string) => request<{ ok: true }>(`/bots/${botId}/sources/${sourceId}/crawl`, { method: 'POST' }),
  indexSelected: (botId: string, sourceId: string) =>
    request<{ ok: true; pages: number }>(`/bots/${botId}/sources/${sourceId}/index`, { method: 'POST' }),
  listPages: (botId: string, sourceId: string) => request<{ pages: SourcePage[] }>(`/bots/${botId}/sources/${sourceId}/pages`),
  setPageSelection: (botId: string, sourceId: string, updates: Array<{ pageId: string; selected: boolean }>) =>
    request<{ pages: SourcePage[] }>(`/bots/${botId}/sources/${sourceId}/pages`, { method: 'PATCH', body: json({ updates }) }),
  downloadUrl: (botId: string, sourceId: string) =>
    request<{ url: string; expiresAt: number }>(`/bots/${botId}/sources/${sourceId}/download`),

  listLaunchers: (botId: string) => request<{ launchers: UploadedLauncher[] }>(`/bots/${botId}/launchers`),
  uploadLauncher: (botId: string, file: File) => {
    const form = new FormData();
    form.set('file', file);
    return request<{ launcher: UploadedLauncher }>(`/bots/${botId}/launchers`, { method: 'POST', body: form });
  },
  deleteLauncher: (botId: string, file: string) =>
    request<{ ok: true }>(`/bots/${botId}/launchers/${encodeURIComponent(file)}`, { method: 'DELETE' }),

  listQa: (botId: string) => request<{ overrides: QaOverride[] }>(`/bots/${botId}/qa`),
  createQa: (botId: string, question: string, answer: string) =>
    request<{ override: QaOverride }>(`/bots/${botId}/qa`, { method: 'POST', body: json({ question, answer }) }),
  updateQa: (botId: string, qaId: string, question: string, answer: string) =>
    request<{ ok: true }>(`/bots/${botId}/qa/${qaId}`, { method: 'PATCH', body: json({ question, answer }) }),
  deleteQa: (botId: string, qaId: string) => request<{ ok: true }>(`/bots/${botId}/qa/${qaId}`, { method: 'DELETE' }),

  listConversations: (
    botId: string,
    opts: { limit?: number; offset?: number; from?: string; to?: string; unansweredOnly?: boolean; archived?: boolean } = {},
  ) => {
    const params = new URLSearchParams();
    if (opts.archived) params.set('view', 'archived');
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.offset) params.set('offset', String(opts.offset));
    if (opts.from) params.set('from', opts.from);
    if (opts.to) params.set('to', opts.to);
    if (opts.unansweredOnly) params.set('filter', 'unanswered');
    const qs = params.toString();
    return request<{ conversations: ConversationRow[]; total: number; limit: number; offset: number; archivedCount: number; archiveKeepMs: number }>(
      `/bots/${botId}/conversations${qs ? `?${qs}` : ''}`,
    );
  },
  /** "Delete": moves conversations to Archived, restorable for three days. */
  archiveConversations: (botId: string, target: { ids: string[] } | { all: true }) =>
    request<{ archived: number; batch: number; archiveKeepMs: number }>(`/bots/${botId}/conversations/archive`, {
      method: 'POST',
      body: json(target),
    }),
  restoreConversations: (botId: string, target: ConversationTarget) =>
    request<{ restored: number }>(`/bots/${botId}/conversations/restore`, { method: 'POST', body: json(target) }),
  getProfile: () => request<{ profile: Profile; changes: ProfileChange[] }>('/profile'),
  updateProfile: (details: ProfileDetails) =>
    request<{ profile: Profile; changed: ProfileField[]; changes: ProfileChange[] }>('/profile', {
      method: 'PUT',
      body: JSON.stringify(details),
    }),
  listLeadRecipients: () => request<{ recipients: LeadRecipient[]; max: number; ownerEmail: string }>('/lead-recipients'),
  addLeadRecipient: (email: string) =>
    request<{ recipient: LeadRecipient }>('/lead-recipients', { method: 'POST', body: JSON.stringify({ email }) }),
  resendLeadRecipient: (id: string) =>
    request<{ recipient: LeadRecipient }>(`/lead-recipients/${encodeURIComponent(id)}/resend`, { method: 'POST' }),
  removeLeadRecipient: (id: string) => request<{ ok: true }>(`/lead-recipients/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  listLeads: (opts: { limit?: number; offset?: number } = {}) => {
    const params = new URLSearchParams();
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.offset) params.set('offset', String(opts.offset));
    const qs = params.toString();
    return request<{ leads: Lead[]; total: number; limit: number; offset: number }>(`/leads${qs ? `?${qs}` : ''}`);
  },
  getConversation: (botId: string, conversationId: string) =>
    request<{ conversation: ConversationRow; messages: LoggedMessage[] }>(`/bots/${botId}/conversations/${conversationId}`),
  /** Moves one conversation to Archived (see archiveConversations). */
  deleteConversation: (botId: string, conversationId: string) =>
    request<{ ok: true; archived: true; batch: number; archiveKeepMs: number }>(`/bots/${botId}/conversations/${conversationId}`, {
      method: 'DELETE',
    }),
  purgeConversations: (botId: string) => request<{ ok: true }>(`/bots/${botId}/conversations`, { method: 'DELETE' }),
  logSummary: (botId: string) => request<{ summary: LogSummary }>(`/bots/${botId}/conversations/summary`),
  analytics: (botId: string, month?: string) =>
    request<{ analytics: BotAnalytics }>(`/bots/${botId}/analytics${month ? `?month=${encodeURIComponent(month)}` : ''}`),
  unanswered: (botId: string, days = 30) =>
    request<{ groups: UnansweredGroup[]; days: number; sampled: number; sampleLimit: number }>(
      `/bots/${botId}/conversations/unanswered?days=${days}`,
    ),
};

// ---- the admin's own settings ---------------------------------------------

export interface AdminSettings {
  /** Whose bots the Customer bots list leaves out: email addresses, or
   * "@domain" for a whole domain. Holds the admin's own address until they
   * save a list of their own. */
  hiddenOwners: string[];
  /** How many messages the admin's own bots may answer per period; null =
   * no limit. */
  messageLimits: Record<LimitPeriod, number | null>;
}

// ---- sales demos (admin only — server/src/demos/) -----------------------

export interface Demo {
  botId: string;
  /** The demo page is /demo/<slug>. */
  slug: string;
  prospectName: string;
  siteUrl: string;
  createdAt: number;
  expiresAt: number;
  /** Who the demo's email is addressed to, as named on the prospect's site
   * (server/src/demos/recipient.ts); empty when it names no one clearly. */
  recipientName: string;
  /** When the site was read for that name; null = not yet. */
  recipientCheckedAt: number | null;
  /** The address the demo was built for, when the admin gave one. */
  contactEmail: string | null;
  /** The bot's current name — it can be renamed in the dashboard. */
  botName: string;
  sources: Source[];
}

type DemoFields = Omit<Demo, 'botName' | 'sources'>;

export const adminApi = {
  /** The admin's own page settings (server/src/admin/settings.ts). */
  getSettings: () => requestAt<{ settings: AdminSettings }>('/api/admin', '/settings'),
  /** Saves the settings given; the others stay as they are. */
  saveSettings: (settings: Partial<AdminSettings>) =>
    requestAt<{ settings: AdminSettings }>('/api/admin', '/settings', { method: 'PUT', body: json(settings) }),
  listDemos: () => requestAt<{ demos: Demo[] }>('/api/admin', '/demos'),
  createDemo: (url: string, name?: string) =>
    requestAt<{ demo: DemoFields; bot: Bot; source: Source }>('/api/admin', '/demos', { method: 'POST', body: json({ url, name }) }),
  extendDemo: (botId: string) =>
    requestAt<{ demo: DemoFields }>('/api/admin', `/demos/${encodeURIComponent(botId)}/extend`, { method: 'POST' }),
  /** What building `entries` would do with each line — builds nothing. */
  checkDemoBatch: (entries: string) =>
    requestAt<{ entries: DemoBatchPlanEntry[] }>('/api/admin', '/demos/batch/check', { method: 'POST', body: json({ entries }) }),
  /** One demo per website in `entries` (pasted emails or websites). */
  createDemoBatch: (entries: string) =>
    requestAt<{ results: DemoBatchResult[] }>('/api/admin', '/demos/batch', { method: 'POST', body: json({ entries }) }),
};

/** Why a batch line won't be built; `repeat` and `exists` are duplicates. */
export type DemoBatchSkip = 'repeat' | 'exists' | 'personal' | 'invalid';

/** One line of a checked batch (server/src/demos/batch.ts planBatch). */
export interface DemoBatchPlanEntry {
  input: string;
  email?: string;
  site?: string;
  action: 'build' | 'skip';
  skip?: DemoBatchSkip;
  reason?: string;
  existing?: { slug: string; botId: string; prospectName: string; siteUrl: string; expiresAt: number };
}

/** What happened to one line of a batch (server/src/demos/batch.ts). */
export interface DemoBatchResult {
  input: string;
  email?: string;
  site?: string;
  status: 'created' | 'skipped' | 'failed';
  skip?: DemoBatchSkip;
  reason?: string;
  /** The new demo's page, or the existing one for a site already demoed. */
  slug?: string;
  botId?: string;
}

export interface AdminBotRow {
  id: string;
  name: string;
  userId: number | null;
  ownerEmail: string | null;
  createdAt: number;
  dailyTokenCap: number;
  sourceCount: number;
  chunkCount: number;
  failedSources: number;
  lastRun: number | null;
  usage: { tokensLast24h: number; requestsLastHour: number };
  logs: {
    conversations7d: number;
    messages7d: number;
    errors7d: number;
    ungrounded7d: number;
    ungroundedRate7d: number;
  };
  capUsedFraction: number;
}

export async function fetchAdminOverview(): Promise<{
  bots: AdminBotRow[];
  totals: {
    bots: number;
    chunks: number;
    failedSources: number;
    tokensLast24h: number;
    conversations7d: number;
    messages7d: number;
    errors7d: number;
  };
  usageAvailable: boolean;
}> {
  const res = await fetch('/api/admin/overview');
  if (!res.ok) throw new ApiError(`Request failed (${res.status})`, res.status);
  return res.json();
}
